// SPDX-License-Identifier: MIT
// 2.7.1 item C — module-level agent RUN MANAGER (desktop). The Rust agent child
// already survives navigation (killed only on explicit Cancel or by the next
// claude_agent_stream's kill-leftover). What did NOT survive was the JS side:
// the Tauri Channel callback lived in the panel's closure, so navigating away
// destroyed the live feed and the line capture drifted into dead UI state.
//
// This manager decouples CAPTURE from DISPLAY:
//  - capture (owned here, keyed by projectDir, survives any unmount): the
//    runAgentStream channel, an in-memory buffer of every AgentChannelMsg of the
//    CURRENT run, the per-run onLine callback (session bookkeeping appends) and
//    the per-run onPersist callback (debounced while streaming, flushed
//    immediately on done/error — the session-store save cadence).
//  - display (panels): subscribe(projectDir, cb) replays the buffer then streams
//    live messages; unsubscribe on unmount. Re-attaching after navigation is
//    just a new subscribe.
//
// Single-child semantics: Rust holds ONE agent child globally and
// claude_agent_stream kills any leftover before spawning. The manager therefore
// NEVER calls cancelAgent on a new startRun (a JS-side cancel could land AFTER
// the new child spawned and race-kill it); it only invalidates the replaced
// run's token so the dead channel's late messages are ignored.
import type { AgentChannelMsg } from './desktop-delivery';
import { runAgentStream, cancelAgent } from './desktop-delivery';

export type AgentRunSubscriber = (msg: AgentChannelMsg) => void;

export interface AgentRunStartOpts {
  // true on a follow-up turn → `claude --continue` (the decideRun policy's !fresh).
  continueConversation?: boolean;
  // Capture callback for the run that STARTED it: append the raw line into the
  // session bookkeeping (welcome.ts request.lines). Survives panel unmount by
  // closure — the manager keeps calling it for this run only.
  onLine?: (line: string) => void;
  // Persist callback (session-store save + history refresh). Debounced during
  // streaming; flushed immediately on done/error and before a replacing run.
  onPersist?: () => void;
  // Terminal callback : called ONCE when this run genuinely ends (done/error),
  // owned by the manager so it fires even if the panel was torn down mid-run.
  // NOT called when the run is replaced by a new startRun. Used for the
  // non-blocking cloud-save auto-push (the code must reach Forgejo even if the
  // user navigated away before the build finished).
  onEnd?: (msg: AgentChannelMsg) => void;
}

export interface AgentRunSubscribeOpts {
  // Default true: replay the current run's buffer synchronously before live
  // messages. Pass false when the caller re-hydrates from a richer source
  // (welcome.ts replays the full session history, which already contains the
  // buffered lines via the shared in-memory session objects).
  replay?: boolean;
}

export interface AgentRunManager {
  startRun(projectDir: string, prompt: string, opts?: AgentRunStartOpts): void;
  subscribe(projectDir: string, cb: AgentRunSubscriber, opts?: AgentRunSubscribeOpts): () => void;
  getBuffer(projectDir: string): readonly AgentChannelMsg[];
  isRunning(projectDir: string): boolean;
  // Epoch ms of the in-flight run's start (elapsed-timer base for a re-attached
  // panel), or null when no run is in flight for this projectDir.
  getStartedAt(projectDir: string): number | null;
  cancel(projectDir: string): void;
}

export interface AgentRunManagerDeps {
  runAgentStream: typeof runAgentStream;
  cancelAgent: typeof cancelAgent;
  persistDebounceMs?: number;
}

interface RunState {
  buffer: AgentChannelMsg[];
  subscribers: Set<AgentRunSubscriber>;
  running: boolean;
  startedAt: number;
  // Monotonic token per startRun: the channel closure compares its own token to
  // the state's CURRENT one and drops messages from a replaced (stale) run.
  token: number;
  persistTimer?: ReturnType<typeof setTimeout>;
  onLine?: (line: string) => void;
  onPersist?: () => void;
  onEnd?: (msg: AgentChannelMsg) => void;
}

const PERSIST_DEBOUNCE_MS = 800;

export function createAgentRunManager(deps: AgentRunManagerDeps): AgentRunManager {
  const states = new Map<string, RunState>();
  let nextToken = 1;
  // The projectDir whose run OWNS the single global Rust child (last startRun
  // wins, mirroring claude_agent_stream's kill-leftover). cancel() only kills
  // the child for this dir — see the comment in cancel.
  let currentRunDir: string | null = null;

  const ensure = (projectDir: string): RunState => {
    let st = states.get(projectDir);
    if (!st) {
      st = { buffer: [], subscribers: new Set(), running: false, startedAt: 0, token: 0 };
      states.set(projectDir, st);
    }
    return st;
  };

  const notify = (st: RunState, msg: AgentChannelMsg): void => {
    // Snapshot: a subscriber may self-unsubscribe (or throw) while being notified.
    for (const cb of [...st.subscribers]) {
      try { cb(msg); } catch (err) { console.warn('[agent-run] subscriber failed', err); }
    }
  };

  const schedulePersist = (st: RunState): void => {
    if (st.persistTimer) clearTimeout(st.persistTimer);
    st.persistTimer = setTimeout(() => {
      st.persistTimer = undefined;
      st.onPersist?.();
    }, deps.persistDebounceMs ?? PERSIST_DEBOUNCE_MS);
  };

  const flushPersist = (st: RunState): void => {
    if (st.persistTimer) { clearTimeout(st.persistTimer); st.persistTimer = undefined; }
    st.onPersist?.();
  };

  const endRun = (st: RunState): void => {
    st.running = false;
    flushPersist(st);
  };

  // Fire the terminal callback once, guarded against throwing. Called only at
  // genuine completion sites (NOT on the replaced-run swap).
  const fireEnd = (st: RunState, msg: AgentChannelMsg): void => {
    try { st.onEnd?.(msg); } catch (err) { console.warn('[agent-run] onEnd failed', err); }
  };

  const startRun = (projectDir: string, prompt: string, opts: AgentRunStartOpts = {}): void => {
    const st = ensure(projectDir);
    if (st.running) {
      // Replaced run: save what it captured so far with ITS persist callback
      // before the swap; its channel messages are then dead (token mismatch).
      endRun(st);
    }
    const token = nextToken++;
    st.token = token;
    currentRunDir = projectDir; // this run now owns the single global child

    st.buffer = [];
    st.running = true;
    st.startedAt = Date.now();
    st.onLine = opts.onLine;
    st.onPersist = opts.onPersist;
    st.onEnd = opts.onEnd;

    deps
      .runAgentStream(prompt, projectDir, (msg) => {
        if (st.token !== token) return; // stale channel of a replaced run
        st.buffer.push(msg);
        if (msg.event === 'line') {
          try { st.onLine?.(msg.data.line); } catch (err) { console.warn('[agent-run] onLine failed', err); }
          schedulePersist(st);
        } else {
          endRun(st);
        }
        notify(st, msg);
        if (msg.event !== 'line') fireEnd(st, msg);
      }, opts.continueConversation ?? false)
      .then(() => {
        // The invoke resolved without a final channel message (the real Rust
        // command always sends Done first — this covers degraded/test shims).
        if (st.token !== token || !st.running) return;
        const msg: AgentChannelMsg = { event: 'done', data: { code: 0 } };
        st.buffer.push(msg);
        endRun(st);
        notify(st, msg);
        fireEnd(st, msg);
      })
      .catch((err) => {
        if (st.token !== token || !st.running) return;
        const msg: AgentChannelMsg = { event: 'error', data: { message: String(err) } };
        st.buffer.push(msg);
        endRun(st);
        notify(st, msg);
        fireEnd(st, msg);
      });
  };

  const subscribe = (projectDir: string, cb: AgentRunSubscriber, opts: AgentRunSubscribeOpts = {}): (() => void) => {
    const st = ensure(projectDir);
    st.subscribers.add(cb);
    if (opts.replay !== false) {
      for (const msg of [...st.buffer]) {
        try { cb(msg); } catch (err) { console.warn('[agent-run] replay subscriber failed', err); }
      }
    }
    return () => { st.subscribers.delete(cb); };
  };

  const cancel = (projectDir: string): void => {
    const st = states.get(projectDir);
    if (!st?.running) return;
    // cancelAgent kills the SINGLE global child — only the project whose run is
    // the CURRENT one may invoke it. A dir can still read running:true in the
    // window after another project's startRun replaced it but before its killed
    // child's late Done{-1} is reaped; killing there would murder the NEW
    // project's child (review fix I3). Either way the dir's state settles below.
    if (currentRunDir === projectDir) {
      void deps.cancelAgent().catch((err) => console.warn('[agent-run] cancel failed', err));
    }
    // Flip the state now for a snappy UI; the killed child's reaped Done{-1}
    // still flows through (same token) so the display shows the outcome.
    endRun(st);
  };

  return {
    startRun,
    subscribe,
    getBuffer: (projectDir) => states.get(projectDir)?.buffer ?? [],
    isRunning: (projectDir) => states.get(projectDir)?.running ?? false,
    getStartedAt: (projectDir) => {
      const st = states.get(projectDir);
      return st?.running ? st.startedAt : null;
    },
    cancel,
  };
}

// Default singleton over the real Tauri-backed functions. Lazy so importing
// this module costs nothing on the web build (the fns themselves only resolve
// window.__TAURI__ at call time anyway).
let singleton: AgentRunManager | undefined;
export function agentRunManager(): AgentRunManager {
  return (singleton ??= createAgentRunManager({ runAgentStream, cancelAgent }));
}
